import { motion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { KineticWords } from "./KineticWords";

const banks = [
  { name: "Komerční banka", logo: "/logos/kb.png" },
  { name: "ČSOB", logo: "/logos/csob.png" },
  { name: "Česká spořitelna", logo: "/logos/ceska-sporitelna.png" },
  { name: "Moneta", logo: "/logos/moneta.png" },
  { name: "Raiffeisenbank", logo: "/logos/raiffeisen.png" },
  { name: "UniCredit Bank", logo: "/logos/unicredit.png" },
  { name: "Air Bank", logo: "/logos/airbank.png" },
  { name: "Fio banka", logo: "/logos/fio.png" },
  { name: "mBank", logo: "/logos/mbank.png" },
];

const invests = [
  { name: "AMISTA", logo: "/logos/inv-amista.png" },
  { name: "J&T Banka", logo: "/logos/inv-jt-banka.png" },
  { name: "CODYA", logo: "/logos/inv-codya.png" },
  { name: "Investika", logo: "/logos/inv-investika.png" },
  { name: "Amundi", logo: "/logos/inv-amundi.png" },
  { name: "Avant", logo: "/logos/inv-avant.png" },
  { name: "Conseq", logo: "/logos/inv-conseq.png" },
  { name: "Investona", logo: "/logos/inv-investona.png" },
  { name: "Wood & Company", logo: "/logos/inv-wood.png" },
  { name: "KK Investment Partners", logo: "/logos/inv-kk-investment.png" },
  { name: "Atris", logo: "/logos/inv-atris.png" },
];

const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

/** Entrance animation: fade-only on mobile (y/rotace překrývaly sousední obsah), full spring on desktop. */
const enter = (delay = 0) =>
  isMobile
    ? {
        initial: { opacity: 0 },
        whileInView: { opacity: 1 },
        viewport: { once: true, margin: "-30px" },
        transition: { duration: 0.4, delay },
      }
    : {
        initial: { opacity: 0, y: 40, scale: 0.92 },
        whileInView: { opacity: 1, y: 0, scale: 1 },
        viewport: { once: true, margin: "-40px" },
        transition: { type: "spring", stiffness: 170, damping: 16, delay },
      };

export const Partners = () => {
  const { t, lang } = useLanguage();

  return (
    <section id="partners" data-testid="partners-section" className="relative overflow-hidden py-28 sm:py-36 lg:py-44">
      <div className="pointer-events-none absolute right-1/4 top-1/3 h-[420px] w-[520px] rounded-full blur-[150px]" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.05) 0%, transparent 65%)" }} aria-hidden="true" />
      <div className="mesh-orb orb-a absolute -left-32 top-16 h-[400px] w-[400px] rounded-full" style={{ background: "rgba(45,212,191,0.12)" }} aria-hidden="true" />
      <div className="mesh-orb orb-b absolute -right-40 bottom-0 h-[440px] w-[440px] rounded-full" style={{ background: "rgba(30,58,95,0.5)" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "14%", top: "26%", width: 3, height: 3, animationDelay: "1.1s" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "80%", top: "18%", width: 2, height: 2, animationDelay: "2.3s" }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12">
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-teal-400">
          03 · {t.partners.badge[lang]}
        </span>
        <h2
          data-testid="partners-heading"
          className="font-display mt-6 max-w-5xl text-[26px] font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          <KineticWords key={lang} text={t.partners.heading[lang]} />
        </h2>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{t.partners.subtext[lang]}</p>

        <div className="mt-20 grid gap-6 lg:grid-cols-12">
          <motion.div
            {...(isMobile
              ? { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true, margin: "-30px" }, transition: { duration: 0.4 } }
              : { initial: { opacity: 0, y: 70, rotate: -2 }, whileInView: { opacity: 1, y: 0, rotate: 0 }, viewport: { once: true, margin: "-60px" }, transition: { type: "spring", stiffness: 100, damping: 16 } })}
            className="lg:col-span-4"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500">
              {t.partners.insuranceLabel[lang]}
            </p>
            <div
              data-testid="partner-logo-allianz"
              className="group mt-5 flex min-h-[220px] items-center justify-center rounded-3xl border border-slate-200 bg-white px-10 py-12 shadow-xl transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_0_50px_rgba(45,212,191,0.3)] lg:h-full lg:min-h-0"
            >
              <img
                src="/logos/allianz.png"
                alt="Allianz"
                className="h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105 sm:h-20"
              />
            </div>
          </motion.div>

          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500">
                {t.partners.banksLabel[lang]}
              </p>
            </Reveal>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {banks.map((b, i) => (
                <motion.div
                  key={b.name}
                  {...enter(i * 0.05)}
                  data-testid={`partner-logo-${i}`}
                  className="flex h-24 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-teal-400 hover:shadow-[0_0_28px_rgba(45,212,191,0.25)]"
                >
                  <img src={b.logo} alt={b.name} className="max-h-11 w-auto max-w-full object-contain" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-16 text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500">
            {t.partners.investLabel[lang]}
          </p>
        </Reveal>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="invest-logos">
          {invests.map((b, i) => (
            <motion.div
              key={b.name}
              {...enter(i * 0.04)}
              data-testid={`invest-logo-${i}`}
              className="flex h-24 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-teal-400 hover:shadow-[0_0_28px_rgba(45,212,191,0.25)]"
            >
              <img src={b.logo} alt={b.name} className="max-h-10 w-auto max-w-full object-contain" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
